import { Lock, NotebookPen } from 'lucide-react';
import type { FocusPhase } from '@/lib/focus/focusMachine';
import { FocusRing } from '../focus/FocusRing';
import { WELLBEING_PATHS } from '../wellbeingPaths';
import { HubTile } from './HubTile';

export interface FocusTileProps {
  phase: FocusPhase;
  /** Time left (or elapsed, in Flow); the mode's length when idle. */
  timeLabel: string;
  /** 0–1 done, `null` for Flow. */
  progress: number | null;
  intention?: string;
  todayMinutes: number;
  todayBlocks: number;
}

const RING_TONE = {
  idle: 'paused',
  focus: 'focus',
  paused: 'paused',
  break: 'break',
} as const;

const PHASE_LABEL: Record<FocusPhase, string> = {
  idle: 'Ready',
  focus: 'Focus',
  paused: 'Paused',
  break: 'Break',
};

const FOCUS_CTA: Record<FocusPhase, string> = {
  idle: 'Start a focus block',
  focus: 'Back to your block',
  paused: 'Back to your block',
  break: 'Open your break',
};

/** The real timer ring, live, beside today's numbers. */
export function FocusTile({
  phase,
  timeLabel,
  progress,
  intention,
  todayMinutes,
  todayBlocks,
}: FocusTileProps) {
  const active = phase !== 'idle';
  return (
    <HubTile
      to={WELLBEING_PATHS.focus}
      title='Focus'
      description={
        active && intention ? (
          <>
            On <span className='text-foreground'>{intention}</span>
          </>
        ) : (
          'Pomodoro blocks with the breaks built in.'
        )
      }
      cta={FOCUS_CTA[phase]}
      tone='deep'
      className='md:col-span-2 lg:col-span-1'
      status={
        <dl className='flex flex-wrap gap-x-4 gap-y-1 whitespace-nowrap text-muted-foreground'>
          <div className='flex items-baseline gap-1.5'>
            <dt>Focused today</dt>
            <dd className='font-mono font-semibold tabular-nums text-foreground'>
              {todayMinutes} min
            </dd>
          </div>
          <div className='flex items-baseline gap-1.5'>
            <dt>Blocks</dt>
            <dd className='font-mono font-semibold tabular-nums text-foreground'>
              {todayBlocks}
            </dd>
          </div>
        </dl>
      }
    >
      <FocusRing
        progress={progress}
        tone={RING_TONE[phase]}
        size='sm'
        className='mx-0'
      >
        <span className='font-mono text-lg font-semibold tabular-nums leading-none text-foreground'>
          {timeLabel}
        </span>
        <span className='mt-1 text-xs font-medium leading-none text-muted-foreground'>
          {PHASE_LABEL[phase]}
        </span>
      </FocusRing>
    </HubTile>
  );
}

/** Rings breathing at the exercise's own pace: the tool, previewed. */
export function BreatheTile() {
  return (
    <HubTile
      to={WELLBEING_PATHS.breathe}
      title='Breathe'
      description='A guided minute to settle your body.'
      cta='Take a breath'
      tone='mist'
      status={
        <span className='text-muted-foreground'>
          1 minute of cyclic sighing
        </span>
      }
    >
      <span
        className='relative flex h-24 w-24 items-center justify-center rounded-full bg-brand-100/70 dark:bg-brand-500/10'
        aria-hidden='true'
      >
        <span className='absolute inset-3 rounded-full bg-brand-200/60 dark:bg-brand-500/15' />
        <span className='h-12 w-12 animate-breathe rounded-full bg-gradient-to-br from-brand-200 to-brand-400 motion-reduce:animate-none dark:from-brand-400/60 dark:to-brand-600/60' />
      </span>
    </HubTile>
  );
}

/** How many thoughts are waiting - never their text, which is private. */
export function WorryTile({ count }: { count: number }) {
  return (
    <HubTile
      to={WELLBEING_PATHS.worry}
      title='Worry dump'
      description="Write down what's on your mind, then set it aside."
      cta={count === 0 ? 'Clear your head' : 'Revisit or add'}
      tone='card'
      status={
        count === 0 ? (
          <span className='flex items-center gap-1.5 text-muted-foreground'>
            <Lock className='h-3.5 w-3.5' aria-hidden='true' />
            Private to this device
          </span>
        ) : (
          <span className='text-muted-foreground'>
            {count === 1 ? 'thought' : 'thoughts'} set aside
          </span>
        )
      }
    >
      <span
        className='flex h-24 w-24 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-500/10'
        aria-hidden={count === 0 ? 'true' : undefined}
      >
        {count === 0 ? (
          <NotebookPen
            className='h-8 w-8 text-brand-500 dark:text-brand-300'
            aria-hidden='true'
          />
        ) : (
          <span className='font-mono text-3xl font-semibold tabular-nums text-foreground'>
            {count}
          </span>
        )}
      </span>
    </HubTile>
  );
}
